
import React, { createContext, useContext, useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, name: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Session refresh interval (every 10 minutes)
    const refreshInterval = setInterval(async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession) {
        // Check if token is close to expiry (within 5 minutes)
        const expiresAt = currentSession.expires_at;
        if (expiresAt) {
          const expiresAtMs = expiresAt * 1000;
          const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
          
          if (expiresAtMs < fiveMinutesFromNow) {
            // Token is about to expire, refresh it
            const { data, error } = await supabase.auth.refreshSession();
            if (error) {
              console.log("Session refresh failed, clearing state");
              setSession(null);
              setUser(null);
            } else if (data.session) {
              setSession(data.session);
              setUser(data.session.user);
            }
          }
        }
      } else {
        // No session, clear state
        if (user) {
          setSession(null);
          setUser(null);
        }
      }
    }, 10 * 60 * 1000); // Check every 10 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const signUp = async (email: string, password: string, name: string) => {
    const redirectUrl = `${window.location.origin}/supplier/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name: name
        }
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { error };
  };

  const signOut = async () => {
    // Clear local state first to prevent stale redirects
    setUser(null);
    setSession(null);
    
    // Then attempt server-side signout (ignore errors for stale sessions)
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Session may already be invalid on server, that's okay
      console.log("Sign out completed");
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, signUp, signIn, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
